import { gql } from '@apollo/client';
import { JobDiversity, JobStatus, JobVisa } from 'shared/models/job';
import {
    AggregateStats,
    ResultSort,
    SearchConfig,
    SearchKeywordSets,
    SearchProject,
    SearchStatus
} from 'shared/models/search';

export interface Search {
    id: string;
    config: SearchConfig;
    jobId: string;
    minimumSkillScore: number;
    maxProfileAgeInDays: number;
    name: string;
    querySql: string;
    agentSql: string;
    strategy: string;
    aggregateStats: AggregateStats;
    keywordSets?: SearchKeywordSets;
    conversationId: string;
    status: SearchStatus;
    sortRank: number;
    resultsSort: ResultSort;
    createdBy: string;
    userId: string;
    project: SearchProject;
    reviewRequiredForOutreach: boolean;
    importRequestId?: string;
    importRequest?: { spark: { name: string; url: string; batch: number } };
    searchProfilesScoreId: string;
    minSearchProfileScore: number;
}

export interface LinkedinSearchTag {
    tag: string;
    count: number;
}

export interface SearchPerson {
    url: string;
    person: {
        id: string;
        name: { first: string; full: string; last: string };
        profilePicUrl: string;
    };
}

export interface LinkedinSearch {
    tag: string;
    count: number;
    results: SearchPerson[];
    results_aggregate: {
        aggregate: {
            min: {
                viewedAt: number;
            };
        };
    };
}

export interface ScrapeReport {
    profiles_scanned: number;
    net_new: number;
    duplicates_fresh: number;
    duplicates_stale: number;
    linkedin_results_min: number | null;
    linkedin_results_max: number | null;
}

export interface SearchConfigData {
    id: string;
    config: SearchConfig;
    keywordSets?: SearchKeywordSets;
}

export interface JobData {
    id: string;
    title: string;
    clientId: string;
    accountManagerId: string;
    status: JobStatus;
    discipline: string;
    client: {
        id: string;
        name: string;
        linkedinUrl: string;
        description: string;
        salesPitch: string;
        foundedDate: string;
        founders: string;
        funding: string;
        hq: string;
        teamSize: string;
        website: string;
    };
    outreachAIContext: {
        directions: string;
        keyCriteria: string;
        highlights: string;
    };
    phoneScreenAIContext: {
        directions: string;
    };
    scoringAIContext: {
        scoringBrief: string;
        briefSource: 'agent' | 'recruiter';
        briefUpdatedAt: number;
    };
    description: {
        location: string;
        description: string;
        minExperienceYears: number;
        maxExperienceYears: number;
        compensation: string;
        visa: JobVisa[];
        hiringManager: string;
        interviewPrep: string;
        diversity: JobDiversity;
    };
}

export const SEARCH_LINKEDIN_TAGS = gql`
    query SearchLinkedinTags($search: String!) {
        tags: search_linkedin_search_tag(args: { search: $search }, where: { count: { _gte: "5" } }) {
            tag
            count
        }
    }
`;

export const LINKEDIN_SEARCHES = gql`
    query LinkedinSearches($tag: String, $profileUrl: String, $limit: Int, $offset: Int) {
        searchesCount: linkedin_search_tag_count(args: { search: $tag, profileUrl: $profileUrl }) {
            result
        }
        searches: search_linkedin_search_tag(
            args: { search: $tag, profileUrl: $profileUrl, limit: $limit, offset: $offset }
        ) {
            tag
            count
            results(limit: 5, order_by: [{ personId: desc }], distinct_on: [personId]) {
                url
                person {
                    id
                    name
                    profilePicUrl
                }
            }
            results_aggregate {
                aggregate {
                    min {
                        viewedAt
                    }
                }
            }
        }
        urls: profile_urls(where: { url: { _eq: $profileUrl } }) {
            url
            person {
                id
                name
                profilePicUrl
            }
        }
    }
`;

export const LINKEDIN_SEARCH_RESULTS = gql`
    query LinkedinSearchResults($tag: String!) {
        results: profile_scraping_extension_scraping_tracker(
            where: { tag: { _eq: $tag } }
            order_by: [{ personId: desc }]
            distinct_on: [personId]
        ) {
            personId
        }
    }
`;

export const UPDATE_LINKEDIN_SEARCH_TAG = gql`
    mutation UpdateLinkedinSearchTag($oldTag: String!, $newTag: String!) {
        update_profile_scraping_extension_scraping_tracker(where: { tag: { _eq: $oldTag } }, _set: { tag: $newTag }) {
            affected_rows
        }
    }
`;

export const GET_SCRAPE_REPORT = gql`
    query GetScrapeReport($tag: String!) {
        report: get_scrape_report(args: { tag_input: $tag }) {
            profiles_scanned
            net_new
            duplicates_fresh
            duplicates_stale
            linkedin_results_min
            linkedin_results_max
        }
    }
`;

export const SEARCH = gql`
    query Search($searchId: String!) {
        search: searches_by_pk(id: $searchId) {
            id
            config
            jobId
            minimumSkillScore
            maxProfileAgeInDays
            name
            querySql
            agentSql
            strategy
            aggregateStats
            keywordSets
            conversationId
            status
            sortRank
            resultsSort
            createdBy
            userId
            project
            reviewRequiredForOutreach
            importRequestId
            importRequest {
                spark
            }
            searchProfilesScoreId
            minSearchProfileScore
        }
    }
`;

export const SEARCH_CONFIG = gql`
    query SearchConfig($searchId: String!) {
        search: searches_by_pk(id: $searchId) {
            id
            config
            keywordSets
        }
    }
`;

export const JOB_DATA = gql`
    query JobData($jobId: String!) {
        job: jobs_by_pk(id: $jobId) {
            id
            title
            clientId
            status
            discipline
            scoringAIContext
            outreachAIContext
            phoneScreenAIContext
            accountManagerId
            client {
                id
                name
                linkedinUrl
            }
            description {
                id: jobId
                description
                location
                minExperienceYears
                maxExperienceYears
                compensation
                visa
                hiringManager
                interviewPrep
                diversity
            }
        }
    }
`;
